import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { tokenConfig } from '../../actions/authActions';
import { connect } from 'react-redux';
import { Users, UserCheck, ChevronDown, ChevronUp, Search, Ban, Trash2, RotateCcw } from 'lucide-react';

const ReferralsTab = ({ auth }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, has_referrals, no_referrals

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const config = {
        headers: { 'Content-Type': 'application/json' }
      };
      const token = auth.token;
      if (token) config.headers['Authorization'] = 'Bearer ' + token;

      const res = await axios.get('/api/referrals/admin/all', config);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch referral data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getAuthConfig = () => {
    const config = { headers: { 'Content-Type': 'application/json' } };
    if (auth.token) config.headers['Authorization'] = 'Bearer ' + auth.token;
    return config;
  };

  const handleDisable = async (id) => {
    try {
      await axios.put(`/api/referrals/admin/${id}/disable`, {}, getAuthConfig());
      fetchReferralData();
    } catch (err) {
      console.error('Failed to disable referral:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this referral code?')) return;
    try {
      await axios.delete(`/api/referrals/admin/${id}`, getAuthConfig());
      fetchReferralData();
    } catch (err) {
      console.error('Failed to delete referral:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-[#1086a4] rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-12 text-gray-500">Failed to load referral data.</div>;
  }

  const { totals, summary, referrals } = data;

  // Filter summary
  const filteredSummary = summary.filter(item => {
    const name = `${item.user?.name || ''} ${item.user?.lastName || ''} ${item.user?.email || ''}`.toLowerCase();
    const matchesSearch = !search || name.includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'has_referrals' && item.used > 0) ||
      (statusFilter === 'no_referrals' && item.used === 0);
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-[#054581]">Referral Tracking</h2>
        <p className="text-sm text-gray-500 mt-1">Track who referred whom and referral code usage</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-[#054581] to-[#1086a4] rounded-xl p-5 text-white">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-lg p-2">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <div className="text-2xl font-bold">{totals.totalReferrers}</div>
              <div className="text-white/70 text-sm">Active Referrers</div>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-5 text-white">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-lg p-2">
              <UserCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="text-2xl font-bold">{totals.totalUsed}</div>
              <div className="text-white/70 text-sm">Successful Referrals</div>
            </div>
          </div>
        </div>
      </div>

      {/* Conversion Rate */}
      {totals.totalShared > 0 && (
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Avg. Signups per Code</span>
            <span className="text-sm font-bold text-[#054581]">
              {(totals.totalUsed / totals.totalShared).toFixed(1)}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-[#1086a4] to-emerald-500 h-2 rounded-full transition-all"
              style={{ width: `${Math.min((totals.totalUsed / totals.totalShared) * 20, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-[#1086a4]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-[#1086a4]"
        >
          <option value="all">All Referrers</option>
          <option value="has_referrals">With Successful Referrals</option>
          <option value="no_referrals">No Successful Referrals Yet</option>
        </select>
      </div>

      {/* Referrer List */}
      {filteredSummary.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {summary.length === 0 ? 'No referral activity yet.' : 'No results match your filters.'}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredSummary.map((item) => {
            const userId = item.user?._id;
            const isExpanded = expandedUser === userId;

            return (
              <div key={userId} className="border border-gray-200 rounded-xl overflow-hidden">
                {/* User Row */}
                <button
                  onClick={() => setExpandedUser(isExpanded ? null : userId)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-[#1086a4]/10 flex items-center justify-center shrink-0">
                      <span className="text-sm font-bold text-[#1086a4]">
                        {`${item.user?.name?.[0] || ''}${item.user?.lastName?.[0] || ''}`.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-left min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {item.user?.name} {item.user?.lastName}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{item.user?.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <span className="text-xs text-gray-500">{item.shared} {item.shared === 1 ? 'code' : 'codes'}</span>
                      <span className="mx-1.5 text-gray-300">·</span>
                      <span className={`text-xs font-semibold ${item.used > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                        {item.used} joined
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Details */}
                {isExpanded && item.referrals.length > 0 && (
                  <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">People who joined via referral</div>
                    <div className="space-y-2">
                      {item.referrals.map((ref, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-800 truncate">
                              {ref.usedBy?.name} {ref.usedBy?.lastName}
                            </div>
                            <div className="text-xs text-gray-500 truncate">{ref.usedBy?.email}</div>
                          </div>
                          <div className="flex items-center gap-4 shrink-0 ml-4">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${ref.usedBy?.skillsBuildClaimedAt ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                              {ref.usedBy?.skillsBuildClaimedAt ? 'IBM Claimed' : 'Not Claimed'}
                            </span>
                            <div className="text-right">
                              <div className="text-xs text-gray-400">
                                {new Date(ref.usedAt).toLocaleDateString()}
                              </div>
                              <div className="text-[10px] text-gray-300 font-mono">{ref.code}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {isExpanded && item.referrals.length === 0 && (
                  <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 text-center text-sm text-gray-400">
                    Codes shared but no one has joined yet.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* All Referrals Table */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">All Referral Codes</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Code</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Shared By</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Signups</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map((ref) => (
                <tr key={ref._id} className={`border-b border-gray-100 hover:bg-gray-50 ${ref.status === 'disabled' ? 'opacity-50' : ''}`}>
                  <td className={`py-2 px-3 font-mono text-xs ${ref.status === 'disabled' ? 'line-through text-gray-400' : 'text-gray-600'}`}>{ref.code}</td>
                  <td className="py-2 px-3 text-gray-700">
                    {ref.owner?.name} {ref.owner?.lastName}
                  </td>
                  <td className="py-2 px-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      ref.status === 'disabled'
                        ? 'bg-red-100 text-red-700'
                        : (ref.usages?.length || 0) > 0
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {ref.status === 'disabled' ? 'Disabled' : (ref.usages?.length || 0) > 0 ? 'Active' : 'Shared'}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-gray-700">
                    {(ref.usages?.length || 0) > 0 ? (
                      <span className="text-sm font-medium text-emerald-600">{ref.usages.length} {ref.usages.length === 1 ? 'signup' : 'signups'}</span>
                    ) : '—'}
                  </td>
                  <td className="py-2 px-3 text-gray-500 text-xs">
                    {new Date(ref.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-1">
                      {ref.status !== 'used' && (
                        <button
                          onClick={() => handleDisable(ref._id)}
                          title={ref.status === 'disabled' ? 'Re-enable' : 'Disable'}
                          className={`p-1 rounded hover:bg-gray-200 transition-colors ${ref.status === 'disabled' ? 'text-emerald-500 hover:text-emerald-700' : 'text-amber-500 hover:text-amber-700'}`}
                        >
                          {ref.status === 'disabled' ? <RotateCcw className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(ref._id)}
                        title="Delete"
                        className="p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {referrals.length === 0 && (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-400">No referral codes yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth
});

export default connect(mapStateToProps)(ReferralsTab);
